import React, { useState, useEffect, createContext, ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import CloudScapeAppLayout from "@cloudscape-design/components/app-layout";
import enMessages from "@cloudscape-design/components/i18n/messages/all.en";
import { I18nProvider } from "@cloudscape-design/components/i18n";
import { Auth } from "aws-amplify";
import useAuth from "../hooks/useAuth";
import FederatedSignIn from "../components/Login/FederatedSignIn";
import { UserMetadata } from "../data/UserMetadata";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import TopNavBar from "../components/TopNavBar/TopNavBar";
import SideNavBar from "../components/SideNavBar/SideNavBar";
import { Notifications } from "../components/Notifications/Notifications";
import { UserProvider } from "../context/UserProvider";
import { PanelContextProvider } from "../context/PanelContextProvider";

function App() {
  const [navOpen, setNavOpen] = useState(false);
  const [toolsPanelOpen, setToolsPanelOpen] = useState<boolean>(false);
  const [toolsPanelContent, setToolsPanelContent] =
    useState<React.ReactNode>(null);
  const [splitPanelOpen, setSplitPanelOpen] = useState<boolean>(false);
  const [splitPanelContent, setSplitPanelContent] =
    useState<React.ReactNode>(null);
  const [user, setUser] = useState<UserMetadata>();
  const location = useLocation();
  const { userData } = useAuth();

  //Todo Need to work on breadcurmbs as separate CR
  const breadcrumbs = useBreadcrumbs({
    items: [
      {
        text: "ServiceNow IRM",
        href: "https://dev.sn.irm.a2z.com/",
      },
      {
        text: location.pathname.substring(1),
        href: location.pathname,
      },
    ],
  });

  useEffect(() => {
    if (!userData) return;
    setUser({
      ...userData,
    });
  }, [userData]);

  if (userData) {
    return (
      <I18nProvider
        messages={[enMessages]}
        locale="en"
        data-testid="localization-provider"
      >
        <UserProvider
          user={user ?? { ...userData, alias: "" }}
          data-testid="user-provider"
        >
          <React.Fragment>
            <TopNavBar />
            <CloudScapeAppLayout
              //Navigation Props
              navigationOpen={navOpen}
              navigation={<SideNavBar />}
              onNavigationChange={({ detail }) => setNavOpen(detail.open)}
              //Page Header Content
              headerSelector="#h"
              breadcrumbs={breadcrumbs}
              notifications={<Notifications />}
              stickyNotifications
              //Page Content
              contentType="table"
              content={
                <PanelContextProvider
                  panelContext={{
                    toolsPanelOpen,
                    setToolsPanelOpen,
                    setToolsPanelContent,
                    splitPanelOpen,
                    setSplitPanelOpen,
                    setSplitPanelContent,
                  }}
                >
                  <Outlet />
                </PanelContextProvider>
              }
              //Tools Panel
              toolsOpen={toolsPanelOpen}
              onToolsChange={() => setToolsPanelOpen((prevState) => !prevState)}
              tools={toolsPanelContent}
              toolsHide={!toolsPanelOpen}
              //Split Panel
              splitPanelOpen={splitPanelOpen}
              onSplitPanelToggle={() => {
                setSplitPanelOpen((prevState) => !prevState);
                setSplitPanelContent(null);
              }}
              splitPanel={splitPanelContent}
              //data id for component unit testing
              data-testid="applayout-component"
            />
          </React.Fragment>
        </UserProvider>
      </I18nProvider>
    );
  } else {
    return <FederatedSignIn authModule={Auth} />;
  }
}
export default App;
